import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const GOOGLE_CLIENT_ID = "";
const FACEBOOK_APP_ID = "";
const TURNSTILE_SITE_KEY = ""; 

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ user: '', password: '' });
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');

  // Función auxiliar para redirigir al usuario al destino correcto tras autenticarse
  const redireccionarTrasLogin = () => {
    const queryParams = new URLSearchParams(location.search);
    const redirectUrl = queryParams.get('redirect');
    const tieneReservaGuardada = sessionStorage.getItem('reserva_temp');

    if (redirectUrl && tieneReservaGuardada) {
      // Venía del flujo de reserva -> va directo a pagar con sus datos guardados
      navigate(redirectUrl, { replace: true });
    } else {
      // Inició sesión de forma directa -> va a la pantalla principal
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    // 1. Carga de FontAwesome
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css';
    document.head.appendChild(link);

    // 2. Carga del SDK de Google
    const scriptGoogle = document.createElement('script');
    scriptGoogle.src = 'https://accounts.google.com/gsi/client';
    scriptGoogle.async = true;
    scriptGoogle.defer = true;
    scriptGoogle.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse
        });

        const btnContainer = document.getElementById('google-btn-container');
        if (btnContainer) {
          window.google.accounts.id.renderButton(btnContainer, {
            type: 'icon',
            shape: 'circle',
            theme: 'filled_black',
            size: 'large'
          });
        }
      }
    };
    document.head.appendChild(scriptGoogle);

    // 3. Carga del SDK de Facebook
    window.fbAsyncInit = function() {
      window.FB.init({
        appId      : FACEBOOK_APP_ID,
        cookie     : true,
        xfbml      : true,
        version    : 'v19.0'
      });
    };

    const scriptFb = document.createElement('script');
    scriptFb.id = 'facebook-jssdk';
    scriptFb.src = 'https://connect.facebook.net/es_LA/sdk.js';
    scriptFb.async = true;
    scriptFb.defer = true;
    document.head.appendChild(scriptFb);

    // 4. Carga del SDK de Cloudflare Turnstile
    const scriptTurnstile = document.createElement('script');
    scriptTurnstile.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    scriptTurnstile.async = true;
    scriptTurnstile.defer = true;
    scriptTurnstile.onload = () => {
      if (window.turnstile) {
        window.turnstile.render('#turnstile-container', {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'dark',
          callback: (token) => {
            setCaptchaToken(token);
            setError('');
          },
          'expired-callback': () => {
            setCaptchaToken('');
          }
        });
      }
    };
    document.head.appendChild(scriptTurnstile);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleResponse = async (response) => {
    try {
      const res = await axios.post('https://backend-bambos.onrender.com/api/auth/google/', {
        token: response.credential
      });

      const { access, role, username } = res.data;

      localStorage.setItem('token', access);
      localStorage.setItem('role', role);
      localStorage.setItem('username', username);

      redireccionarTrasLogin();
    } catch (err) {
      console.error(err);
      setError('Error al autenticar con Google. Inténtalo de nuevo.');
    }
  };

  const handleFacebookLogin = () => {
    if (!window.FB) {
      setError('El SDK de Facebook aún no se ha cargado. Intenta de nuevo.');
      return;
    }

    window.FB.login((response) => {
      if (response.authResponse) {
        axios.post('https://backend-bambos.onrender.com/api/auth/facebook/', {
          token: response.authResponse.accessToken
        })
        .then((res) => {
          const { access, role, username } = res.data;

          localStorage.setItem('token', access);
          localStorage.setItem('role', role);
          localStorage.setItem('username', username);

          redireccionarTrasLogin();
        })
        .catch((err) => {
          console.error(err);
          setError('Error al autenticar con Facebook. Inténtalo de nuevo.');
        });
      }
    }, { scope: 'email,public_profile' });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('Por favor, completa la verificación de seguridad (Captcha).');
      return;
    }

    try {
      const response = await axios.post('https://backend-bambos.onrender.com/api/login/', {
        ususis_username: form.user,
        password: form.password,
        cf_turnstile_response: captchaToken
      });

      const { access, role, username } = response.data;

      localStorage.setItem('token', access);
      localStorage.setItem('role', role);
      localStorage.setItem('username', username);

      redireccionarTrasLogin();

    } catch (err) {
      console.error(err);
      if (err.response && err.response.data.detail) {
          setError(err.response.data.detail);
      } else {
          setError('Usuario o contraseña incorrectos');
      }
    }
  };

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: '40px', textAlign: 'center' }}>
         <img src="/img/LogoBambo.png" alt="Bambo Logo" style={{ width: '120px', marginBottom: '10px' }} 
              onError={(e) => e.target.style.display = 'none'}/>
         <h2 style={{ letterSpacing: '3px', fontWeight: '900', color: '#FFD700' }}>BIENVENIDO</h2>
      </div>

      <div style={cardStyle}>
        {error && <div style={errorBoxStyle}>{error}</div>}
        
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>USUARIO / EMAIL</label>
            <div style={{ position: 'relative' }}>
              <i className="fa-solid fa-user" style={iconInsideInput}></i>
              <input 
                type="text" 
                placeholder="Ej: Minombre@mail.com" 
                style={inputStyle}
                onChange={(e) => setForm({...form, user: e.target.value})}
                required
              />
            </div>
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>CONTRASEÑA</label>
            <div style={{ position: 'relative' }}>
              <i className="fa-solid fa-lock" style={iconInsideInput}></i>
              <input 
                type="password" 
                placeholder="••••••••" 
                style={inputStyle}
                onChange={(e) => setForm({...form, password: e.target.value})}
                required
              />
            </div>
          </div>

          <div id="turnstile-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}></div>

          <button type="submit" style={btnSubmit}>INGRESAR</button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button 
            onClick={() => navigate('/recuperar-cuenta')} 
            style={linkBtnStyle}
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        <div style={dividerStyle}>
          <div style={lineStyle}></div>
          <span style={{ padding: '0 10px', fontSize: '12px' }}>O INGRESA CON</span>
          <div style={lineStyle}></div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
          <div id="google-btn-container" style={{ width: '48px', height: '48px' }}></div>

          <button onClick={handleFacebookLogin} style={socialBtn} title="Ingresar con Facebook">
            <i className="fa-brands fa-facebook" style={{ color: '#1877F2' }}></i>
          </button>
        </div>

        <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#aaa', marginBottom: '10px' }}>¿Usuario Nuevo?</p>
          <button onClick={() => navigate('/registro')} style={registerBtnStyle}>Registrarse</button>
        </div>
      </div>

      <button onClick={() => navigate('/')} style={backBtnStyle}>← Volver al inicio</button>
    </div>
  );
}

// ESTILOS
const containerStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#000', color: '#fff', fontFamily: 'Arial, sans-serif' };
const cardStyle = { width: '100%', maxWidth: '400px', padding: '40px', backgroundColor: '#111', borderRadius: '15px', border: '1px solid #333', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };
const labelStyle = { fontSize: '12px', color: '#FFD700', fontWeight: 'bold', marginLeft: '5px' };
const inputStyle = { width: '100%', padding: '12px 12px 12px 40px', backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', fontSize: '16px', color: '#fff', outline: 'none', boxSizing: 'border-box' };
const iconInsideInput = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#444' };
const btnSubmit = { backgroundColor: '#FFD700', color: '#000', padding: '15px', border: 'none', borderRadius: '8px', fontWeight: '900', fontSize: '16px', cursor: 'pointer', marginTop: '10px', textTransform: 'uppercase' };
const dividerStyle = { display: 'flex', alignItems: 'center', margin: '30px 0', color: '#444' };
const lineStyle = { flex: 1, height: '1px', backgroundColor: '#333' };
const socialBtn = { width: '48px', height: '48px', borderRadius: '50%', border: '1px solid #333', backgroundColor: '#111', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const registerBtnStyle = { color: '#FFD700', fontWeight: 'bold', fontSize: '24px', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' };
const backBtnStyle = { marginTop: '30px', color: '#666', background: 'none', border: 'none', cursor: 'pointer' };
const errorBoxStyle = { color: '#ff4444', textAlign: 'center', fontSize: '14px', marginBottom: '15px', padding: '10px', backgroundColor: 'rgba(255, 68, 68, 0.1)', borderRadius: '5px' };
const linkBtnStyle = { color: '#aaa', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'none' };

export default LoginPage;